import { lazy } from "react";
import { Route, Routes } from "react-router-dom";

import AppLayout from "@/components/AppLayout";
import SubAppLayout from "@/components/SubAppLayout";
import Dashboard from "@/routes/Dashboard";
import Debug from "@/routes/Debug";
import NotFound from "@/routes/NotFound";

// Route-level code splitting, replacing next/dynamic. SubAppLayout already
// renders a <Suspense> boundary with a per-app loader, so no extra fallback is
// needed at the route level.
const BoardConferencesView = lazy(
  () => import("@/modules/board/components/ConferencesView"),
);
const BoardConferenceDetailsView = lazy(
  () => import("@/modules/board/components/ConferenceDetailsView"),
);
const BoardDocumentsView = lazy(
  () => import("@/modules/board/components/DocumentsView"),
);
const HonorariumConferencesView = lazy(
  () => import("@/modules/honorarium/components/ConferencesView"),
);
const HonorariumConferenceDetailsView = lazy(
  () => import("@/modules/honorarium/components/ConferenceDetailsView"),
);
const PeopleDetails = lazy(
  () => import("@/modules/people/components/PeopleDetails"),
);
const PeopleReferrals = lazy(
  () => import("@/modules/people/components/PeopleReferrals"),
);
const TaxFormsUpcomingConferences = lazy(
  () => import("@/modules/tax-forms/components/UpcomingConferencesUnified"),
);
const TaxFormsConferenceRegistration = lazy(
  () => import("@/modules/tax-forms/components/ConferenceRegistration"),
);
const TaxFormsViewForm = lazy(
  () => import("@/modules/tax-forms/components/ViewForm"),
);
const LibertyAIChatView = lazy(
  () => import("@/modules/liberty-ai/components/ChatView"),
);

/**
 * Mirrors the former `pages/` tree one-for-one. Dynamic segments that were
 * `[conferenceId]` file names are now `:conferenceId` params read with
 * `useParams()` instead of `useRouter().query`.
 */
export default function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="debug" element={<Debug />} />

        {/*
          Auth0 redirects back here after login. Auth0Provider consumes the
          ?code=&state= querystring and its onRedirectCallback navigates on to
          the originally requested route, so this only ever renders for the
          instant before that happens.
        */}
        <Route path="callback" element={null} />

        <Route
          path="liberty-ai"
          element={
            <SubAppLayout appName="liberty-ai">
              <LibertyAIChatView />
            </SubAppLayout>
          }
        />

        <Route path="board">
          <Route
            path="conferences"
            element={
              <SubAppLayout appName="board">
                <BoardConferencesView />
              </SubAppLayout>
            }
          />
          <Route
            path="conferences/:conferenceId"
            element={
              <SubAppLayout appName="board">
                <BoardConferenceDetailsView />
              </SubAppLayout>
            }
          />
          <Route
            path="documents"
            element={
              <SubAppLayout appName="board">
                <BoardDocumentsView />
              </SubAppLayout>
            }
          />
        </Route>

        <Route path="honorarium">
          <Route
            path="conferences"
            element={
              <SubAppLayout appName="honorarium">
                <HonorariumConferencesView />
              </SubAppLayout>
            }
          />
          <Route
            path="conferences/:conferenceId"
            element={
              <SubAppLayout appName="honorarium">
                <HonorariumConferenceDetailsView />
              </SubAppLayout>
            }
          />
        </Route>

        <Route path="people">
          <Route
            path="referrals"
            element={
              <SubAppLayout appName="people">
                <PeopleReferrals />
              </SubAppLayout>
            }
          />
          <Route
            path="bio/:personId"
            element={
              <SubAppLayout appName="people">
                <PeopleDetails />
              </SubAppLayout>
            }
          />
        </Route>

        <Route path="tax-forms">
          <Route
            path="conferences"
            element={
              <SubAppLayout appName="tax-forms">
                <TaxFormsUpcomingConferences />
              </SubAppLayout>
            }
          />
          <Route
            path="conferences/:conferenceId/:confereeId"
            element={
              <SubAppLayout appName="tax-forms">
                <TaxFormsConferenceRegistration />
              </SubAppLayout>
            }
          />
          <Route
            path="view/:confereeId/:formName"
            element={
              <SubAppLayout appName="tax-forms">
                <TaxFormsViewForm />
              </SubAppLayout>
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
